import type { ComponentProps } from 'react';

import { LocationPinIcon } from '@change/ds-icons';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui/next';
import { ComboBox, ComboBoxItem } from '@change/ds-forms/next';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { useEditLocationModal } from './hooks/useEditLocationModal';

type EditLocationModalProps = Required<Pick<ComponentProps<typeof Modal>, 'trigger'>>;

export const EditLocationModal: React.FC<EditLocationModalProps> = ({ trigger }) => {
	const { translate } = useI18n();
	const { data, actions } = useEditLocationModal();

	return (
		<Modal
			onClose={actions.resetModal}
			trigger={trigger}
			size="large"
			className="overflow-y-auto [&>div>div]:!max-h-[initial] [&>div>div]:flex-[1_0_auto] [&>div>div]:md:!max-h-[700px] [&_section]:h-full"
		>
			<ModalHeader
				title={translate('corgi.homepage.local_section.modal.header.title')}
				subtitle={translate('corgi.homepage.local_section.modal.header.subtitle')}
			/>
			<ModalBody className="!max-h-[initial] flex-[1_0_auto] px-1 md:min-h-[318px]">
				<EditLocationModalBody data={data} actions={actions} />
			</ModalBody>
			<ModalFooter>
				{({ close }) => (
					<Button
						variant="primary"
						onClick={async () => actions.handleLocationSubmit(close)}
						data-qa="edit-location-modal-primary-action"
					>
						<Translate value="corgi.homepage.local_section.modal.footer.primary_action" />
					</Button>
				)}
			</ModalFooter>
		</Modal>
	);
};

const EditLocationModalBody: React.FC<{
	data: ReturnType<typeof useEditLocationModal>['data'];
	actions: ReturnType<typeof useEditLocationModal>['actions'];
}> = ({ data, actions }) => {
	const { translate } = useI18n();

	return (
		<div className="w-full">
			<ComboBox
				className="w-full"
				id="petition-place"
				name="petition-place"
				label={translate('corgi.homepage.local_section.modal.body.search_input.label')}
				labelIsVisible
				inputValue={data.placeInputValue}
				onInputChange={(newValue) => actions.handlePlaceInputChange(newValue)}
				onSelectionChange={(newValueKey) => actions.handlePlaceChange(newValueKey)}
				items={data.places}
				errorMessage={data.formError}
				isInvalid={!!data.formError}
				data-qa="edit-location-modal-combobox"
				renderEmptyState={() => (
					<Text as="div" className="border-b border-t border-transparent px-3 py-3.5">
						<Translate value="design-system.combobox.no_options_label" />
					</Text>
				)}
				showCaretDownIcon={false}
				allowsEmptyCollection
				showSearchIcon
			>
				{(item) => <ComboBoxItem key={item.id} id={item.id} label={item.name} leadingIcon={<LocationPinIcon />} />}
			</ComboBox>
		</div>
	);
};
